import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    CircularProgress,
    Alert,
    TextField,
    Slider,
    Tooltip,
    Chip,
    FormControl,
    FormControlLabel,
    FormLabel,
    Radio,
    RadioGroup,
} from '@mui/material';
import { TreeView, TreeItem } from '@mui/x-tree-view';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';
import AddIcon from '@mui/icons-material/Add';
import { useHelpContent } from '../../utils/HelpUtils';
import { Category } from '../../types/Category';
import {
    categoryPickerNodeExpandable,
    contentCategoryMayHaveRows,
    contentCountForPickerDisplay,
    findCategoryInTree,
} from '../../utils/CategoryUtils';
import {
    branchChildrenAreLoaded,
    categoryContentsAreLoaded,
} from '../../utils/categoryLoadState';
import {
    fetchCategoryForPane,
    mergeBranchFetchIntoTree,
    mergeContentsFetchIntoTree,
} from '../../services/CategoryService';

export type ExpandImportMode = 'category_tree' | 'content_only';
export type ExpandContentTargetMode = 'existing' | 'new_subcategory';

export interface ExpandContentTarget {
    mode: ExpandContentTargetMode;
    targetCategoryId?: string;
    newSubcategoryName?: string;
}

interface ExpandTreeDialogProps {
    open: boolean;
    onClose: () => void;
    onProceed: (
        context: string,
        maxDepth: number,
        treeStrategy: 'top_down' | 'bottom_up',
        importMode: ExpandImportMode,
        contentTarget?: ExpandContentTarget,
        charsPerSegment?: number,
    ) => Promise<void>;
    fileNames: string[];
    currentDocumentName: string;
    documentCategoryId?: string;
}

const DEFAULT_CHARS_PER_SEGMENT = 600;
const MIN_CHARS_PER_SEGMENT = 100;
const MAX_CHARS_PER_SEGMENT = 20000;

function isContentCategoryNode(category: Category): boolean {
    return category.contentCategory === true || (category as { isContentCategory?: boolean }).isContentCategory === true;
}

const ExpandTreeDialog: React.FC<ExpandTreeDialogProps> = ({
    open,
    onClose,
    onProceed,
    fileNames,
    currentDocumentName,
    documentCategoryId,
}) => {
    const [context, setContext] = useState('');
    const [maxDepth, setMaxDepth] = useState(1);
    const [charsPerSegment, setCharsPerSegment] = useState<number>(DEFAULT_CHARS_PER_SEGMENT);
    const [treeStrategy, setTreeStrategy] = useState<'top_down' | 'bottom_up'>('bottom_up');
    const [importMode, setImportMode] = useState<ExpandImportMode>('category_tree');
    const [contentTargetMode, setContentTargetMode] = useState<ExpandContentTargetMode>('existing');
    const [targetCategoryId, setTargetCategoryId] = useState('');
    const [newSubcategoryName, setNewSubcategoryName] = useState('');
    const [pickerTree, setPickerTree] = useState<Category[]>([]);
    const [expandedNodeIds, setExpandedNodeIds] = useState<string[]>([]);
    const [treeLoadingIds, setTreeLoadingIds] = useState<Set<string>>(new Set());
    const [loadingCategories, setLoadingCategories] = useState(false);
    const [treeError, setTreeError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const { getTooltipTextForState } = useHelpContent();
    const pickerTreeRef = useRef<Category[]>([]);
    pickerTreeRef.current = pickerTree;
    const loadGenRef = useRef(0);

    const needsCategoryPicker =
        open &&
        !!documentCategoryId &&
        importMode === 'content_only' &&
        contentTargetMode === 'existing';

    const needsRootMeta =
        open &&
        !!documentCategoryId &&
        importMode === 'content_only';

    const documentRoot = pickerTree[0] ?? null;

    const rootHasSubcategories = useMemo(() => {
        if (!documentRoot || isContentCategoryNode(documentRoot)) return false;
        if (branchChildrenAreLoaded(documentRoot)) {
            return (documentRoot.children?.length ?? 0) > 0;
        }
        return documentRoot.hasChildCategories === true;
    }, [documentRoot]);

    const hydrateContentCategoryCounts = useCallback(async (categoryIds: string[]) => {
        const toLoad = categoryIds.filter((id) => {
            const live = findCategoryInTree(pickerTreeRef.current, id);
            return (
                !!live?.contentCategory &&
                !categoryContentsAreLoaded(live) &&
                contentCategoryMayHaveRows(live)
            );
        });
        if (toLoad.length === 0) return;

        setTreeLoadingIds((prev) => {
            const next = new Set(prev);
            toLoad.forEach((id) => next.add(id));
            return next;
        });
        try {
            const results = await Promise.all(
                toLoad.map(async (id) => {
                    try {
                        return { id, loaded: await fetchCategoryForPane(id) };
                    } catch {
                        return null;
                    }
                }),
            );
            setPickerTree((prev) => {
                let next = prev;
                for (const result of results) {
                    if (!result) continue;
                    next = mergeContentsFetchIntoTree(next, result.id, result.loaded);
                }
                pickerTreeRef.current = next;
                return next;
            });
        } finally {
            setTreeLoadingIds((prev) => {
                const next = new Set(prev);
                toLoad.forEach((id) => next.delete(id));
                return next;
            });
        }
    }, []);

    const ensurePickerNodeLoaded = useCallback(
        async (categoryId: string) => {
            const live = findCategoryInTree(pickerTreeRef.current, categoryId);
            if (!live) return null;
            if (live.contentCategory) {
                if (categoryContentsAreLoaded(live)) return live;
                setTreeLoadingIds((prev) => new Set(prev).add(categoryId));
                try {
                    const loaded = await fetchCategoryForPane(categoryId);
                    setPickerTree((prev) => {
                        const next = mergeContentsFetchIntoTree(prev, categoryId, loaded);
                        pickerTreeRef.current = next;
                        return next;
                    });
                    return loaded;
                } finally {
                    setTreeLoadingIds((prev) => {
                        const next = new Set(prev);
                        next.delete(categoryId);
                        return next;
                    });
                }
            }
            if (branchChildrenAreLoaded(live)) return live;
            setTreeLoadingIds((prev) => new Set(prev).add(categoryId));
            try {
                const loaded = await fetchCategoryForPane(categoryId);
                setPickerTree((prev) => {
                    const next = mergeBranchFetchIntoTree(prev, categoryId, loaded);
                    pickerTreeRef.current = next;
                    return next;
                });
                const contentIds = (loaded.children ?? [])
                    .filter((c) => c.contentCategory && contentCategoryMayHaveRows(c))
                    .map((c) => c.id);
                if (contentIds.length > 0) {
                    void hydrateContentCategoryCounts(contentIds);
                }
                return loaded;
            } finally {
                setTreeLoadingIds((prev) => {
                    const next = new Set(prev);
                    next.delete(categoryId);
                    return next;
                });
            }
        },
        [hydrateContentCategoryCounts],
    );

    // Load only the document root (one level) when content-only mode needs placement options.
    useEffect(() => {
        if (!needsRootMeta || !documentCategoryId) {
            if (!open) {
                setPickerTree([]);
                pickerTreeRef.current = [];
                setExpandedNodeIds([]);
                setTreeError(null);
                setLoadingCategories(false);
            }
            return;
        }

        // Already have this document root loaded.
        if (pickerTreeRef.current[0]?.id === documentCategoryId) {
            return;
        }

        const gen = ++loadGenRef.current;
        setLoadingCategories(true);
        setTreeError(null);

        void (async () => {
            try {
                const root = await fetchCategoryForPane(documentCategoryId);
                if (gen !== loadGenRef.current) return;
                setPickerTree([root]);
                pickerTreeRef.current = [root];
                setExpandedNodeIds([root.id]);
                if (isContentCategoryNode(root)) {
                    setTargetCategoryId(root.id);
                    setContentTargetMode('existing');
                } else {
                    const contentIds = (root.children ?? [])
                        .filter((c) => c.contentCategory && contentCategoryMayHaveRows(c))
                        .map((c) => c.id);
                    if (contentIds.length === 1) {
                        setTargetCategoryId(contentIds[0]);
                    }
                    if (contentIds.length > 0) {
                        void hydrateContentCategoryCounts(contentIds);
                    }
                }
            } catch (error) {
                console.error('Failed to load document categories for expand dialog', error);
                if (gen === loadGenRef.current) {
                    setPickerTree([]);
                    pickerTreeRef.current = [];
                    setTreeError(
                        error instanceof Error ? error.message : 'Failed to load categories',
                    );
                }
            } finally {
                if (gen === loadGenRef.current) {
                    setLoadingCategories(false);
                }
            }
        })();
    }, [needsRootMeta, documentCategoryId, open, hydrateContentCategoryCounts]);

    // Clear picker when leaving content-only mode.
    useEffect(() => {
        if (importMode !== 'content_only') {
            setPickerTree([]);
            pickerTreeRef.current = [];
            setExpandedNodeIds([]);
            setTargetCategoryId('');
            setTreeError(null);
            setLoadingCategories(false);
        }
    }, [importMode]);

    useEffect(() => {
        if (importMode === 'content_only' && !rootHasSubcategories) {
            setContentTargetMode('existing');
        }
    }, [importMode, rootHasSubcategories]);

    // Hydrate counts for visible content categories under expanded nodes.
    useEffect(() => {
        if (!needsCategoryPicker || pickerTree.length === 0) return;
        const expanded = new Set(expandedNodeIds);
        const ids: string[] = [];
        const walk = (cat: Category, parentExpanded: boolean) => {
            if (cat.contentCategory) {
                if (
                    parentExpanded &&
                    contentCategoryMayHaveRows(cat) &&
                    !categoryContentsAreLoaded(cat)
                ) {
                    ids.push(cat.id);
                }
                return;
            }
            if (!expanded.has(cat.id)) return;
            for (const child of cat.children ?? []) {
                walk(child, true);
            }
        };
        for (const root of pickerTree) {
            if (root.contentCategory) walk(root, true);
            else if (expanded.has(root.id)) {
                for (const child of root.children ?? []) walk(child, true);
            }
        }
        if (ids.length > 0) {
            void hydrateContentCategoryCounts(ids);
        }
    }, [needsCategoryPicker, pickerTree, expandedNodeIds, hydrateContentCategoryCounts]);

    const handleNodeToggle = (_event: React.SyntheticEvent, nodeIds: string[]) => {
        setExpandedNodeIds(nodeIds);
        const newlyExpanded = nodeIds.filter((id) => !expandedNodeIds.includes(id));
        for (const id of newlyExpanded) {
            const cat = findCategoryInTree(pickerTreeRef.current, id);
            if (cat && !cat.contentCategory && !branchChildrenAreLoaded(cat)) {
                void ensurePickerNodeLoaded(id);
            } else if (cat && !cat.contentCategory && branchChildrenAreLoaded(cat)) {
                const contentIds = (cat.children ?? [])
                    .filter((c) => c.contentCategory && contentCategoryMayHaveRows(c))
                    .map((c) => c.id);
                if (contentIds.length > 0) {
                    void hydrateContentCategoryCounts(contentIds);
                }
            }
        }
    };

    const selectContentCategory = useCallback((categoryId: string) => {
        if (!categoryId || categoryId.endsWith('__loading')) return;
        const cat = findCategoryInTree(pickerTreeRef.current, categoryId);
        if (cat && !cat.contentCategory) return;
        if (cat && !contentCategoryMayHaveRows(cat) && cat.id !== documentCategoryId) return;
        setTargetCategoryId(categoryId);
        if (cat?.contentCategory && !categoryContentsAreLoaded(cat)) {
            void ensurePickerNodeLoaded(categoryId);
        }
    }, [documentCategoryId, ensurePickerNodeLoaded]);

    const handleNodeSelect = (_event: React.SyntheticEvent, nodeIdInput: string | string[]) => {
        const nodeId = Array.isArray(nodeIdInput) ? nodeIdInput[0] : nodeIdInput;
        if (!nodeId) return;
        selectContentCategory(nodeId);
    };

    const renderPickerNode = (category: Category): React.ReactNode | null => {
        const isContent = isContentCategoryNode(category);
        if (isContent && !contentCategoryMayHaveRows(category) && category.id !== documentCategoryId) {
            return null;
        }
        const selectable = isContent && contentCategoryMayHaveRows(category);
        const expandable = categoryPickerNodeExpandable(category);
        const childrenLoaded = branchChildrenAreLoaded(category);
        const hasLoadedChildren = (category.children?.length ?? 0) > 0;
        const loading = treeLoadingIds.has(category.id);
        const rowCount = contentCountForPickerDisplay(category);
        const isSelected = targetCategoryId === category.id;

        const label = (
            <Box
                onMouseDown={
                    selectable
                        ? (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              selectContentCategory(category.id);
                          }
                        : undefined
                }
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    py: 0.25,
                    cursor: selectable ? 'pointer' : 'default',
                    width: '100%',
                }}
            >
                {isContent ? (
                    <DescriptionIcon fontSize="small" color={isSelected ? 'primary' : 'action'} />
                ) : (
                    <FolderIcon fontSize="small" color="action" />
                )}
                <Typography
                    variant="body2"
                    color={isSelected ? 'primary' : selectable ? 'text.primary' : 'text.secondary'}
                    fontWeight={isSelected ? 600 : 400}
                >
                    {category.name}
                    {loading && (
                        <Typography component="span" variant="caption" color="text.secondary">
                            {' '}
                            (loading…)
                        </Typography>
                    )}
                    {isContent && (
                        <Typography component="span" variant="caption" color="text.secondary">
                            {` (${rowCount === null ? '…' : rowCount})`}
                        </Typography>
                    )}
                    {isSelected ? ' (selected)' : ''}
                </Typography>
            </Box>
        );

        if (isContent) {
            return <TreeItem key={category.id} nodeId={category.id} label={label} />;
        }

        return (
            <TreeItem key={category.id} nodeId={category.id} label={label}>
                {hasLoadedChildren
                    ? category.children!.map((child) => renderPickerNode(child)).filter(Boolean)
                    : null}
                {expandable && !childrenLoaded && !hasLoadedChildren ? (
                    <TreeItem
                        nodeId={`${category.id}__loading`}
                        label={
                            <Typography variant="body2" color="text.secondary">
                                Loading…
                            </Typography>
                        }
                        disabled
                    />
                ) : null}
            </TreeItem>
        );
    };

    const handleProceed = async () => {
        setLoading(true);
        try {
            const contentTarget: ExpandContentTarget | undefined =
                importMode === 'content_only'
                    ? contentTargetMode === 'new_subcategory'
                        ? {
                              mode: 'new_subcategory',
                              newSubcategoryName: newSubcategoryName.trim(),
                          }
                        : {
                              mode: 'existing',
                              targetCategoryId,
                          }
                    : undefined;
            const clampedChars = Math.max(
                MIN_CHARS_PER_SEGMENT,
                Math.min(MAX_CHARS_PER_SEGMENT, charsPerSegment),
            );
            await onProceed(context.trim(), maxDepth, treeStrategy, importMode, contentTarget, clampedChars);
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setContext('');
        setMaxDepth(1);
        setCharsPerSegment(DEFAULT_CHARS_PER_SEGMENT);
        setTreeStrategy('bottom_up');
        setImportMode('category_tree');
        setContentTargetMode('existing');
        setTargetCategoryId('');
        setNewSubcategoryName('');
        setPickerTree([]);
        pickerTreeRef.current = [];
        setExpandedNodeIds([]);
        setTreeError(null);
        setTreeLoadingIds(new Set());
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const isProceedDisabled = () => {
        if (loading) return true;
        if (importMode !== 'content_only') return false;
        if (loadingCategories && contentTargetMode === 'existing') return true;
        if (contentTargetMode === 'new_subcategory') {
            return !newSubcategoryName.trim();
        }
        return !targetCategoryId;
    };

    return (
        <Dialog
            open={open}
            onClose={(_event, reason) => {
                if (reason === 'backdropClick') return;
                handleClose();
            }}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>Add to document from files</DialogTitle>
            <DialogContent>
                <Box sx={{ position: 'relative' }}>
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" gutterBottom>
                            Current Document: <strong>{currentDocumentName}</strong>
                        </Typography>
                        <Typography variant="subtitle1" gutterBottom>
                            Selected files
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                            {fileNames.map((fileName, index) => (
                                <Chip key={index} label={fileName} variant="outlined" size="small" />
                            ))}
                        </Box>
                    </Box>

                </Box>

                <Box sx={{ mb: 2 }}>
                    <FormControl component="fieldset" variant="standard">
                        <FormLabel component="legend">
                            <Tooltip title={getTooltipTextForState('expand-tree-import-mode', true)} placement="top">
                                <span>Import mode</span>
                            </Tooltip>
                        </FormLabel>
                        <RadioGroup
                            value={importMode}
                            onChange={(e) => setImportMode(e.target.value as ExpandImportMode)}
                        >
                            <FormControlLabel
                                value="category_tree"
                                control={<Radio />}
                                label="Integrate with category structure"
                            />
                            <FormControlLabel
                                value="content_only"
                                control={<Radio />}
                                label="Content only (no folders)"
                            />
                        </RadioGroup>
                    </FormControl>
                </Box>

                {importMode === 'content_only' && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" gutterBottom>
                            Place content in
                        </Typography>
                        <RadioGroup
                            value={contentTargetMode}
                            onChange={(e) =>
                                setContentTargetMode(e.target.value as ExpandContentTargetMode)
                            }
                        >
                            <FormControlLabel
                                value="existing"
                                control={<Radio />}
                                label="Existing content category"
                            />
                            {rootHasSubcategories && (
                                <FormControlLabel
                                    value="new_subcategory"
                                    control={<Radio />}
                                    label="New subcategory under document root"
                                />
                            )}
                        </RadioGroup>
                        {contentTargetMode === 'existing' ? (
                            <Box sx={{ mt: 1 }}>
                                {loadingCategories && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                                        <CircularProgress size={18} />
                                        <Typography variant="body2">Loading categories…</Typography>
                                    </Box>
                                )}
                                {treeError && (
                                    <Alert severity="error" sx={{ mb: 1 }}>
                                        {treeError}
                                    </Alert>
                                )}
                                {!loadingCategories && documentRoot && (
                                    <>
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                                            Expand folders and select a content category.
                                            {targetCategoryId
                                                ? ` Selected: ${findCategoryInTree(pickerTree, targetCategoryId)?.name ?? 'category'}`
                                                : ''}
                                        </Typography>
                                        <Box
                                            sx={{
                                                maxHeight: 260,
                                                overflow: 'auto',
                                                border: (theme) => `1px solid ${theme.palette.divider}`,
                                                borderRadius: 1,
                                                p: 0.5,
                                            }}
                                        >
                                            <TreeView
                                                key={documentRoot.id}
                                                defaultCollapseIcon={<ExpandMoreIcon />}
                                                defaultExpandIcon={<ChevronRightIcon />}
                                                expanded={expandedNodeIds}
                                                onNodeToggle={handleNodeToggle}
                                                selected={targetCategoryId || ''}
                                                onNodeSelect={handleNodeSelect}
                                            >
                                                {renderPickerNode(documentRoot)}
                                            </TreeView>
                                        </Box>
                                    </>
                                )}
                                {!loadingCategories && !documentRoot && !treeError && (
                                    <Alert severity="warning" sx={{ mt: 1 }}>
                                        No categories found in this document.
                                    </Alert>
                                )}
                            </Box>
                        ) : (
                            <TextField
                                fullWidth
                                size="small"
                                label="New subcategory name"
                                value={newSubcategoryName}
                                onChange={(e) => setNewSubcategoryName(e.target.value)}
                                placeholder="e.g. Imported notes"
                                sx={{ mt: 1 }}
                            />
                        )}
                    </Box>
                )}

                <Box sx={{ mb: 2 }}>
                    <Tooltip title={getTooltipTextForState('expand-tree-chars-per-section', true)} placement="top">
                        <TextField
                            type="number"
                            label="Characters per section"
                            value={charsPerSegment}
                            onChange={(e) => {
                                const parsed = parseInt(e.target.value, 10);
                                setCharsPerSegment(Number.isNaN(parsed) ? DEFAULT_CHARS_PER_SEGMENT : parsed);
                            }}
                            onBlur={() =>
                                setCharsPerSegment((prev) =>
                                    Math.max(MIN_CHARS_PER_SEGMENT, Math.min(MAX_CHARS_PER_SEGMENT, prev)),
                                )
                            }
                            inputProps={{ min: MIN_CHARS_PER_SEGMENT, max: MAX_CHARS_PER_SEGMENT, step: 100 }}
                            variant="outlined"
                            size="small"
                            helperText={`Target size before starting a new section (default ${DEFAULT_CHARS_PER_SEGMENT})`}
                            sx={{ width: 260 }}
                        />
                    </Tooltip>
                </Box>

                <Tooltip title={getTooltipTextForState('expand-tree-context', true)} placement="top">
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Context (optional)"
                        value={context}
                        onChange={(e) => setContext(e.target.value)}
                        placeholder="e.g. continuation of prior notes; organize by date"
                        variant="outlined"
                        sx={{ mb: 2 }}
                    />
                </Tooltip>

                {importMode === 'category_tree' && (
                <>
                <Box sx={{ mb: 2 }}>
                    <FormControl component="fieldset" variant="standard">
                        <FormLabel component="legend">
                            <Tooltip title={getTooltipTextForState('expand-tree-strategy', true)} placement="top">
                                <span>Building strategy</span>
                            </Tooltip>
                        </FormLabel>
                        <RadioGroup
                            row
                            value={treeStrategy}
                            onChange={(e) => setTreeStrategy(e.target.value as 'top_down' | 'bottom_up')}
                        >
                            <FormControlLabel
                                value="top_down"
                                control={<Radio />}
                                label="Top-down (broad first)"
                            />
                            <FormControlLabel
                                value="bottom_up"
                                control={<Radio />}
                                label="Bottom-up (fine then group, default)"
                            />
                        </RadioGroup>
                    </FormControl>
                </Box>

                <Box sx={{ mb: 1 }}>
                    <Typography variant="subtitle1" gutterBottom>
                        Maximum tree depth: {maxDepth}
                    </Typography>
                    <Tooltip title={getTooltipTextForState('expand-tree-max-depth', true)} placement="top">
                        <Slider
                            value={maxDepth}
                            onChange={(_, value) => setMaxDepth(value as number)}
                            min={1}
                            max={5}
                            marks
                            valueLabelDisplay="auto"
                            sx={{ mt: 1 }}
                        />
                    </Tooltip>
                </Box>
                </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleProceed}
                    variant="contained"
                    disabled={isProceedDisabled()}
                    startIcon={<AddIcon />}
                >
                    Continue
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ExpandTreeDialog;
